import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { timsurveyApi } from '../../services/api'
import { TimsurveyParseEmailResult, TimsurveyPratica } from '../../types/timsurvey'

interface FormState {
  ragione_sociale: string
  indirizzo: string
  citta: string
  provincia: string
  cap: string
  data_sopralluogo_prevista: string
  referente_cliente: string
  referente_oa: string
  referente_vendita: string
  id_grs_site_survey: string
  n_oda_sopralluogo: string
}

const FORM_VUOTO: FormState = {
  ragione_sociale: '',
  indirizzo: '',
  citta: '',
  provincia: '',
  cap: '',
  data_sopralluogo_prevista: '',
  referente_cliente: '',
  referente_oa: '',
  referente_vendita: '',
  id_grs_site_survey: '',
  n_oda_sopralluogo: '',
}

const CAMPI: { key: keyof FormState; label: string }[] = [
  { key: 'ragione_sociale', label: 'Ragione sociale *' },
  { key: 'indirizzo', label: 'Indirizzo' },
  { key: 'citta', label: 'Città' },
  { key: 'provincia', label: 'Provincia' },
  { key: 'cap', label: 'CAP' },
  { key: 'data_sopralluogo_prevista', label: 'Data sopralluogo prevista' },
  { key: 'referente_cliente', label: 'Referente cliente' },
  { key: 'referente_oa', label: 'Referente OA' },
  { key: 'referente_vendita', label: 'Referente vendita' },
  { key: 'id_grs_site_survey', label: 'ID GRS Site Survey' },
  { key: 'n_oda_sopralluogo', label: 'N° ODA sopralluogo' },
]

export default function TimsurveyNuovaPratica() {
  const navigate = useNavigate()
  const [tab, setTab] = useState<'manuale' | 'email'>('manuale')
  const [form, setForm] = useState<FormState>(FORM_VUOTO)
  const [emailRaw, setEmailRaw] = useState('')
  const [confidence, setConfidence] = useState<Record<string, 'HIGH' | 'MEDIUM' | 'LOW'>>({})
  const [warnings, setWarnings] = useState<string[]>([])
  const [errore, setErrore] = useState('')
  const [analizzando, setAnalizzando] = useState(false)
  const [salvando, setSalvando] = useState(false)

  const setCampo = (key: keyof FormState, value: string) => {
    setForm(f => ({ ...f, [key]: value }))
  }

  const analizzaEmail = async () => {
    if (!emailRaw.trim()) return
    setAnalizzando(true)
    try {
      const res = await timsurveyApi.pratiche.parseEmail(emailRaw)
      const data = res.data as TimsurveyParseEmailResult
      setForm(f => ({ ...f, ...(data.campi as Partial<FormState>) }))
      setConfidence(data.confidence)
      setWarnings(data.warnings ?? [])
      toast.success('Email analizzata, verifica i campi precompilati')
    } catch (err: any) {
      toast.error(err.response?.data?.detail ?? 'Errore analisi email')
    } finally {
      setAnalizzando(false)
    }
  }

  const bordoCampo = (key: keyof FormState): string => {
    if (tab !== 'email') return ''
    const c = confidence[key]
    if (c === 'LOW') return 'border-yellow-400'
    if (!form[key]) return 'border-red-400'
    return ''
  }

  const salva = async () => {
    if (!form.ragione_sociale.trim()) {
      setErrore('Ragione sociale obbligatoria')
      return
    }
    setErrore('')
    setSalvando(true)
    try {
      const body = {
        ...form,
        data_sopralluogo_prevista: form.data_sopralluogo_prevista
          ? new Date(form.data_sopralluogo_prevista).toISOString()
          : null,
        email_raw: tab === 'email' && emailRaw ? emailRaw : null,
      }
      const res = await timsurveyApi.pratiche.create(body)
      const pratica = res.data as TimsurveyPratica
      toast.success('Pratica creata')
      navigate(`/timsurvey/${pratica.id}`)
    } catch (err: any) {
      toast.error(err.response?.data?.detail ?? 'Errore creazione pratica')
    } finally {
      setSalvando(false)
    }
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="page-title">Nuova Pratica Sopralluogo</h1>
        <p className="page-subtitle">Modulo TIM Survey Cabling</p>
      </div>

      <div className="flex gap-2">
        <button
          className={tab === 'manuale' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setTab('manuale')}
        >
          Inserimento manuale
        </button>
        <button
          className={tab === 'email' ? 'btn-primary' : 'btn-secondary'}
          onClick={() => setTab('email')}
        >
          Da email
        </button>
      </div>

      {tab === 'email' && (
        <div className="card p-4 space-y-3">
          <label className="label">Incolla il testo dell'email ricevuta</label>
          <textarea
            className="input w-full"
            rows={8}
            value={emailRaw}
            onChange={e => setEmailRaw(e.target.value)}
            placeholder="Incolla qui il testo grezzo dell'email..."
          />
          <button className="btn-secondary" disabled={analizzando || !emailRaw.trim()} onClick={analizzaEmail}>
            {analizzando ? 'Analisi in corso...' : 'Analizza'}
          </button>
          {warnings.length > 0 && (
            <div className="text-sm bg-yellow-50 border border-yellow-300 text-yellow-800 rounded-md p-3">
              {warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}
        </div>
      )}

      <div className="card p-4 space-y-3">
        {errore && <div className="text-sm text-red-600">{errore}</div>}
        {CAMPI.map(({ key, label }) => (
          <div key={key}>
            <label className="label">{label}</label>
            {key === 'data_sopralluogo_prevista' ? (
              <input
                type="datetime-local"
                className={`input w-full ${bordoCampo(key)}`}
                value={form[key]}
                onChange={e => setCampo(key, e.target.value)}
              />
            ) : (
              <input
                type="text"
                className={`input w-full ${bordoCampo(key)}`}
                value={form[key]}
                onChange={e => setCampo(key, e.target.value)}
              />
            )}
          </div>
        ))}
      </div>

      <button className="btn-primary" disabled={salvando} onClick={salva}>
        {salvando ? 'Creazione in corso...' : 'Crea pratica'}
      </button>
    </div>
  )
}
